/**
 * The three entry-point cards on the Hiring Assistant welcome screen.
 *
 * Single source of truth: the cards render from this, and the chat transcript
 * labels the recruiter's click from it too (live and on history replay, which
 * only stores the raw `value`).
 *
 * The `value`s are load-bearing well beyond this list — the product tour anchors,
 * the "pick up where you left off" flow and the flow_1/flow_2/audit mapping all
 * key off them. Relabel freely; don't rename the values.
 */
export interface WelcomeOption {
  text: string;
  description: string;
  value: string;
}

export const WELCOME_OPTIONS: WelcomeOption[] = [
  {
    text: 'Compare CVs',
    description: 'Evaluate and compare multiple CVs to identify the best candidates to engage.',
    value: 'evaluate',
  },
  {
    text: 'Hire for a Role',
    description: 'Source, screen, and coordinate candidates for an open position from start to finish.',
    value: 'scratch',
  },
  {
    text: 'Run Talent Audit',
    description:
      'Run a structured competency audit of existing employees to assess strengths, gaps, and role fit for talent development and workforce planning.',
    value: 'talent_audit',
  },
];

/**
 * What the recruiter's click should read as in the transcript.
 *
 * The cards are titles ("Run Talent Audit"), but the chat bubble shows the full
 * description — it reads as something the recruiter said, and it's what the
 * assistant is actually responding to. Returns undefined for anything that isn't
 * a welcome option, so callers can fall back to their own label.
 */
export const getWelcomeOptionLabel = (value: string): string | undefined =>
  WELCOME_OPTIONS.find((option) => option.value === value)?.description;

/**
 * Is this transcript line a welcome card's own description rather than something
 * the recruiter typed? The descriptions are long single sentences, so anything
 * measuring "long" content — notably the document-chip rule — has to be able to
 * tell them apart instead of relying on them staying under a length threshold.
 */
export const isWelcomeOptionLabel = (content: string): boolean =>
  WELCOME_OPTIONS.some((option) => option.description === content);
